import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Plus, Briefcase } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = ["Planning", "Not Started", "In Progress", "On Hold", "Under Review", "Completed", "Cancelled"];
const PRIORITY = ["Low", "Medium", "High", "Urgent"];

const empty = { name: "", client_id: "", manager_id: "", team_ids: [], description: "", start_date: "", expected_end_date: "", priority: "Medium", status: "Not Started", progress: 0, budget: 0 };

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState(params.get("status") || "");
  const { user } = useAuth();
  const canCreate = user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    const urlStatus = params.get("status") || "";
    if (urlStatus !== filter) setFilter(urlStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const load = async () => {
    const r = await api.get("/projects", { params: filter ? { status: filter } : {} });
    setProjects(r.data);
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const save = async () => {
    if (!form.name || !form.client_id) { toast.error("Project name and client required"); return; }
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false); setForm(empty); setParams({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Delivery</div>
          <h1 className="text-4xl font-black tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">{projects.length} project{projects.length !== 1 && "s"}</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter || "__all__"} onValueChange={(v) => setFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-44"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {canCreate && (
            <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setParams({}); }}>
              <SheetTrigger asChild><Button data-testid="new-project-btn" className="bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />New Project</Button></SheetTrigger>
              <SheetContent className="sm:max-w-lg overflow-y-auto">
                <SheetHeader><SheetTitle>New Project</SheetTitle></SheetHeader>
                <div className="mt-5 space-y-3">
                  <div><Label className="font-mono text-[10px] uppercase">Name *</Label><Input data-testid="proj-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label className="font-mono text-[10px] uppercase">Client *</Label>
                    <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                      <SelectTrigger data-testid="proj-form-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                      <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="font-mono text-[10px] uppercase">Manager</Label>
                    <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Choose manager" /></SelectTrigger>
                      <SelectContent>{users.filter((u) => u.role !== "member").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="font-mono text-[10px] uppercase">Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                    <div><Label className="font-mono text-[10px] uppercase">Expected end</Label><Input type="date" value={form.expected_end_date} onChange={(e) => setForm({ ...form, expected_end_date: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="font-mono text-[10px] uppercase">Priority</Label>
                      <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PRIORITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="font-mono text-[10px] uppercase">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="font-mono text-[10px] uppercase">Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <Button data-testid="proj-form-submit" onClick={save} className="w-full bg-primary hover:bg-primary/90">Create Project</Button>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>

      {projects.length === 0 ? <EmptyState icon={Briefcase} title="No projects" description="Create your first project to organise tasks and deliverables." /> :
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p) => {
            const client = clients.find((c) => c.id === p.client_id);
            return (
              <div key={p.id} className="border border-border rounded-md p-5 bg-card hover:border-primary/40 transition-colors" data-testid={`project-card-${p.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground font-mono truncate">{client?.company_name || "—"}</div>
                    <div className="font-display font-extrabold text-lg leading-tight mt-0.5">{p.name}</div>
                  </div>
                  <StatusBadge value={p.status} />
                </div>
                <div className="text-xs text-muted-foreground mb-3 font-mono">
                  {p.start_date && <>Start {fmtDate(p.start_date)} · </>}
                  {p.expected_end_date && <>Ends {fmtDate(p.expected_end_date)}</>}
                </div>
                <div>
                  <div className="flex justify-between text-[10px] font-mono uppercase text-muted-foreground mb-1"><span>Progress</span><span>{p.progress || 0}%</span></div>
                  <Progress value={p.progress || 0} />
                </div>
                <div className="flex items-center justify-between mt-4 text-xs">
                  <div className="text-muted-foreground">{p._tasks_completed || 0}/{p._task_count || 0} tasks</div>
                  <StatusBadge value={p.priority} />
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}
